// Register additional boolean client flags here; they remain off until confirmed by Remote Config.
export const booleanFeatureFlagDefaults = {
    publizm_meta_pixel_enabled: false,
} as const;

export type BooleanFeatureFlagKey = keyof typeof booleanFeatureFlagDefaults;
export type BooleanFeatureFlagState = { enabled: boolean; status: "loading" | "ready" | "unavailable" };

export interface BooleanConfigSource {
    fetchFresh(): Promise<void>;
    read(key: BooleanFeatureFlagKey): boolean;
}

const loading: BooleanFeatureFlagState = { enabled: false, status: "loading" };
const unavailable: BooleanFeatureFlagState = { enabled: false, status: "unavailable" };

export class BooleanFeatureFlags {
    private listeners = new Set<() => void>();
    private states = Object.fromEntries(Object.keys(booleanFeatureFlagDefaults).map((key) => [key, loading])) as Record<
        BooleanFeatureFlagKey,
        BooleanFeatureFlagState
    >;
    private generation = 0;

    constructor(private readonly createSource: () => Promise<BooleanConfigSource | null>) {}

    getSnapshot = (key: BooleanFeatureFlagKey): BooleanFeatureFlagState => this.states[key];

    subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);
        if (this.listeners.size === 1) void this.load(++this.generation);
        return () => {
            this.listeners.delete(listener);
            if (this.listeners.size === 0) {
                ++this.generation;
                this.publish(() => loading);
            }
        };
    };

    private publish(next: (key: BooleanFeatureFlagKey) => BooleanFeatureFlagState): void {
        for (const key of Object.keys(booleanFeatureFlagDefaults) as BooleanFeatureFlagKey[]) {
            this.states[key] = next(key);
        }
        this.listeners.forEach((listener) => listener());
    }

    private async load(generation: number): Promise<void> {
        try {
            const source = await this.createSource();
            if (generation !== this.generation) return;
            if (!source) {
                this.publish(() => unavailable);
                return;
            }
            await source.fetchFresh();
            if (generation !== this.generation) return;
            this.publish((key) => ({ enabled: source.read(key), status: "ready" }));
        } catch {
            if (generation === this.generation) this.publish(() => unavailable);
        }
    }
}
